import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import ClipboardText from 'ui-library/ClipboardText';
import {BLUE_LIGHT, BLUE_POWDER, GRAY_DARK, GRAY_LIGHTER} from 'ui-library/Colors';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {WarehouseColumnData} from '../warehouseModelsDataLayout';
import {areColumnsEqual} from '../WarehouseUtils';
import {useDiagramLayoutContext} from './DiagramLayoutContext';
import {sqlColumnTypeToIcon, sqlColumnTypeToString} from './SqlColumnTypeMappers';

export interface WarehouseDiagramTableRowProps {
  columnData: WarehouseColumnData;
}

export default function WarehouseDiagramTableRow(props: WarehouseDiagramTableRowProps): JSX.Element {
  const [isHovered, hoverProps] = useHoverState();
  const {layout} = useDiagramLayoutContext();
  const highlighted = Boolean(
    props.columnData === layout.hoveredRelationship?.source ||
      props.columnData === layout.hoveredRelationship?.target
  );
  const isForeignKey = useMemo(
    () =>
      props.columnData.relationships.some((relationship) =>
        areColumnsEqual(relationship.source, props.columnData)
      ),
    [props.columnData]
  );

  return (
    <div
      className={css(
        warehouseDiagramTableRowStyles.wrapper,
        highlighted && warehouseDiagramTableRowStyles.highlightedWrapper
      )}
      {...hoverProps}
    >
      <div
        className={classNames(
          'u-vertical-center-items',
          css(warehouseDiagramTableRowStyles.columnNameIconWrapper)
        )}
      >
        <BenchlingIcon
          className={css(warehouseDiagramTableRowStyles.typeIcon)}
          icon={sqlColumnTypeToIcon(props.columnData.type)}
          {...getTestProps({component: 'WarehouseDiagramTableRow', element: 'typeIcon'})}
        />
        <div
          className={classNames(
            css(warehouseDiagramTableRowStyles.columnName),
            css(
              isForeignKey
                ? warehouseDiagramTableRowStyles.foreignKeyColumnName
                : warehouseDiagramTableRowStyles.nonForeignKeyColumnName
            )
          )}
          {...getTestProps({component: 'WarehouseDiagramTableRow', element: 'columnName'})}
        >
          <div className="clipboardIcon-wrapper--flex">
            <ClipboardText clipboardData={props.columnData.name} copyIconAlwaysVisible={isHovered}>
              <div className={css(warehouseDiagramTableRowStyles.wrappedColumnName)}>
                {props.columnData.name}
              </div>
            </ClipboardText>
          </div>
        </div>
      </div>
      <div
        className={classNames(css(warehouseDiagramTableRowStyles.columnType), 'u-vertical-center-items')}
        {...getTestProps({component: 'WarehouseDiagramTableRow', element: 'typeText'})}
      >
        {sqlColumnTypeToString(props.columnData.type)}
      </div>
    </div>
  );
}

export const warehouseDiagramTableRowStyles = StyleSheet.create({
  wrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 6px',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
      transition: 'background-color 0.2s',
    },
  },
  highlightedWrapper: {
    backgroundColor: BLUE_POWDER,
  },
  columnNameIconWrapper: {
    display: 'flex',
  },
  columnType: {
    color: GRAY_DARK,
  },
  columnName: {
    marginLeft: 6,
    marginTop: 1,
    whiteSpace: 'nowrap',
  },
  foreignKeyColumnName: {
    color: BLUE_LIGHT,
  },
  nonForeignKeyColumnName: {
    color: 'black',
  },
  wrappedColumnName: {
    overflowX: 'hidden',
    maxWidth: 140,
    textOverflow: 'ellipsis',
  },
  typeIcon: {
    color: GRAY_DARK,
  },
});
